/**
 * Path routing algorithms for dependency lines
 * Uses orthogonal (Manhattan) routing to navigate around items
 */

import { ITEM_HEIGHT, ITEM_LANE_HEIGHT } from '../../TimelineItem';
import type { ItemBounds, Point } from '../types';
import { DEPENDENCY_CONSTANTS } from '../types';
import { getBubblePositions } from './coordinateMapping';

const { HORIZONTAL_PADDING } = DEPENDENCY_CONSTANTS;

/**
 * Generate orthogonal path from source item to target item
 * Avoids overlapping items by routing through vertical gaps
 */
export function generateOrthogonalPath(
  sourceItemId: number,
  targetItemId: number,
  positions: Map<number, ItemBounds>,
): string {
  const source = positions.get(sourceItemId);
  const target = positions.get(targetItemId);

  if (!source || !target) {
    return '';
  }

  // Calculate bubble positions (right edge of source, left edge of target)
  const sourceBubbles = getBubblePositions(source, ITEM_LANE_HEIGHT, ITEM_HEIGHT);
  const targetBubbles = getBubblePositions(target, ITEM_LANE_HEIGHT, ITEM_HEIGHT);

  const startPoint = sourceBubbles.right;
  const endPoint = targetBubbles.left;

  // Generate path based on relative positions
  if (endPoint.x > startPoint.x) {
    // Target is to the right (most common case)
    return generateForwardPath(startPoint, endPoint);
  }

  // Target is to the left (needs to route around)
  return generateBackwardPath(startPoint, endPoint, source, target);
}

/**
 * Generate path when target is to the right of source
 * Routes around items to avoid overlapping
 * Vertical-first routing: goes up/down first, then horizontally
 */
function generateForwardPath(start: Point, end: Point): string {
  // If items are on the same horizontal level, draw straight line
  if (Math.abs(start.y - end.y) < 1) {
    const points: Point[] = [
      start, // Starting point (right bubble of source)
      { x: start.x + HORIZONTAL_PADDING, y: start.y }, // Extend horizontally right
      end, // End point (left bubble of target)
    ];
    return pointsToPathString(points);
  }

  // For items on different levels, route vertically first, then horizontally
  // Move vertically from source level to target level, then connect horizontally
  const routeX = start.x + HORIZONTAL_PADDING;

  const points: Point[] = [
    start, // Starting point (right bubble of source)
    { x: routeX, y: start.y }, // Extend horizontally right
    { x: routeX, y: end.y }, // Move vertically to target level
    end, // End point (left bubble of target)
  ];

  return pointsToPathString(points);
}

/**
 * Generate path when target is to the left of source
 * Routes around both items using vertical space below them
 * Vertical-first routing: goes down first, then horizontally, then up
 */
function generateBackwardPath(
  start: Point,
  end: Point,
  source: ItemBounds,
  target: ItemBounds,
): string {
  // Route below the lower of the two items
  const maxY = Math.max(source.y + source.height, target.y + target.height);

  const points: Point[] = [
    start, // Starting point
    { x: start.x, y: maxY }, // Go down to routing level below items (vertical first)
    { x: end.x, y: maxY }, // Move horizontally across
    end, // Come back to target level (final vertical segment)
  ];

  return pointsToPathString(points);
}

/**
 * Convert array of points to SVG path string
 * M = move to, L = line to
 */
function pointsToPathString(points: Point[]): string {
  if (points.length === 0) return '';

  const [first, ...rest] = points;
  if (!first) return '';

  const moveTo = `M ${first.x},${first.y}`;
  const lines = rest.map((p) => `L ${p.x},${p.y}`).join(' ');

  return `${moveTo} ${lines}`;
}

/**
 * Generate temporary drag line from source bubble to cursor
 */
export function generateDragPath(start: Point, end: Point): string {
  return pointsToPathString([start, end]);
}

/**
 * Calculate arrowhead marker path for dependency direction
 */
export function getArrowheadMarkerDef(): string {
  return `
    <defs>
      <marker
        id="arrowhead"
        markerWidth="6"
        markerHeight="6"
        refX="5"
        refY="2"
        orient="auto"
        markerUnits="strokeWidth"
      >
        <path d="M0,0 L0,4 L5,2 z" fill="currentColor" />
      </marker>
      <marker
        id="arrowhead-green"
        markerWidth="6"
        markerHeight="6"
        refX="5"
        refY="2"
        orient="auto"
        markerUnits="strokeWidth"
      >
        <path d="M0,0 L0,4 L5,2 z" fill="currentColor" />
      </marker>
    </defs>
  `;
}
